import React, {type FC, useState, useRef, useEffect, useCallback} from 'react'
import clsx from 'clsx'
import {useAppSelector} from 'store'
import {getTranslates} from 'store/translates/selectors'
import type {MenuProps} from '../index'
import {BurgerIcon, CheckMarkIcon, CupIcon} from 'uikit/Icons'
import {GameProgress} from 'src/common/components/GameProgress'
import styles from './MobileMenu.scss'

const TIME_TO_CLOSE_MENU = 400

export const MobileMenu: FC<MenuProps> = (props) => {
  const {
    messageStartGame,
    currentLang,
    languages,
    donateMessage,
    handleStartGame,
    handleChangeLanguage,
    handleOpenTop,
    handleDonate,
  } = props

  const [isOpenMobileMenu, setIsOpenMobileMenu] = useState(false)
  const [isVisibleMobileMenu, setIsVisibleMobileMenu] = useState(false)
  const [isOpenLangList, setIsOpenLangList] = useState(false)
  const [isVisibleLangList, setIsVisibleLangList] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)
  const translates = useAppSelector(getTranslates)

  const smoothlyCloseMobileMenu = async () =>
    new Promise((resolve) => {
      setTimeout(() => {
        setIsVisibleMobileMenu(false)
        resolve(1)
      }, TIME_TO_CLOSE_MENU)
    })

  const handleClickBurger = async () => {
    setIsOpenMobileMenu(!isOpenMobileMenu)
    if (isOpenMobileMenu) {
      await smoothlyCloseMobileMenu()
    } else {
      setIsVisibleMobileMenu(true)
    }
  }

  const smoothlyCloseLangList = async () => 
    new Promise((resolve) => {
      setTimeout(() => {
        setIsVisibleLangList(false)
        resolve(1)
      }, TIME_TO_CLOSE_MENU)
    })

  const handleOpenLangList = async () => {
    setIsOpenLangList(!isOpenLangList);
    if (isOpenLangList) {
      await smoothlyCloseLangList()
    } else {
      setIsVisibleLangList(true)
    }
  }

  const handleClickStartGame = useCallback(() => {
    handleClickBurger()
    handleStartGame()
  }, [handleClickBurger, handleStartGame])

  const handleClickDonate = useCallback(() => {
    handleClickBurger()
    handleDonate?.()
  }, [handleClickBurger, handleDonate])

  const handleClickOpenTop = useCallback(() => {
    handleClickBurger()
    handleOpenTop?.()
  }, [handleClickBurger, handleOpenTop])

  useEffect(() => {
    const handleOutsideClick = async (event: MouseEvent) => {
      if (menuRef && !menuRef.current?.contains(event.target as Node) && isOpenMobileMenu) {
        await handleClickBurger()
      }
    }

    document.addEventListener('click', handleOutsideClick)

    return () => {
        document.removeEventListener('click', handleOutsideClick)
    };
  }, [isOpenMobileMenu])

  const selectedOption = languages.find((option) => {
    return currentLang !== undefined ? option.value === currentLang : true
  })

  return (
    <div className={styles.mobileMenuWrapper} ref={menuRef}>
      <div className={styles.burger} onClick={handleClickBurger}>
        <BurgerIcon />
      </div>
      <GameProgress />
      {isVisibleMobileMenu &&
        <ul className={clsx(isOpenMobileMenu ? styles.showMenuClass : styles.hideMenuClass)}>
          <li onClick={handleClickStartGame} key='startGame'>{messageStartGame}</li>
          {handleOpenTop && <li onClick={handleClickOpenTop} key='openTop'><CupIcon />{translates.TOP}</li>}
          <li className={styles.langListWrapper} key='changeLang'>
            <span onClick={handleOpenLangList}>
              {selectedOption?.name}
              <span className={clsx(styles.icon, isVisibleLangList && styles.iconOpened)}></span>
            </span>
            {isVisibleLangList &&
              <ul className={clsx(
                  styles.langList,
                  isOpenLangList ? styles.showLangListClass : styles.hideLangListClass,
                )}
              >
                {languages.map((item) => {
                  const isCurrentLanguage = item.value === currentLang;

                  const handleClickChangeLanguage = async () => {
                    await handleOpenLangList();
                    await handleClickBurger();
                    handleChangeLanguage(item.value);
                  }

                  return (
                    <li
                      key={`${item.name}-${item.value}`}
                      className={clsx(isCurrentLanguage && styles.currentLanguage)}
                      onClick={handleClickChangeLanguage}
                    >
                      {isCurrentLanguage &&
                        <span className={styles.checkMark}><CheckMarkIcon /></span>
                      }
                      {item.name}
                    </li>
                  )
                })}
              </ul>
            }
          </li>
          {handleDonate && <li key='donate' onClick={handleClickDonate} className={styles.donate}>{donateMessage}</li>}
        </ul>
      }
    </div>
  )
}
